/**
 * Halloran's workshop: what Des will do to your motor, and what it costs. A money sink for the offline
 * game; the work persists in the save as the build sheet of "your motor" (the last car Des worked on),
 * which comes back at Halloran's yard — or in your lockup, once you have the key — every session.
 *
 * Looks are visual only (vehicles' `CarLook`, docs/POLISH.md "Vehicles, third pass"). The performance
 * tune is the one thing that touches handling: a small multiplier on power and top speed, offline only
 * (`VehicleTune`, applied by the vehicle system; never online, and spec numbers are never changed).
 */
import type { VehicleSpec } from '../vehicles/types';
import type { VehicleTune } from '../vehicles/vehicleSystem';
import type { CarBuild } from './state';

export const GARAGE_POI = 'garage-halloran';
export const GARAGE_OWNER = 'Des Halloran';

export interface Paint {
  id: string;
  name: string;
  hex: number;
}

/** Period colours (names are the workshop's own). */
export const PAINTS: readonly Paint[] = [
  { id: 'pillarbox', name: 'Pillar-box red', hex: 0xa3201b },
  { id: 'brooklands', name: 'Brooklands green', hex: 0x1f4a2e },
  { id: 'harvest', name: 'Harvest gold', hex: 0xc8a23a },
  { id: 'midnight', name: 'Midnight blue', hex: 0x1a2440 },
  { id: 'tangerine', name: 'Tangerine', hex: 0xd2642a },
  { id: 'bronze', name: 'Conker bronze', hex: 0x7a5230 },
  { id: 'aqua', name: 'Aqua', hex: 0x3d8a8a },
  { id: 'aubergine', name: 'Aubergine', hex: 0x4a2440 },
  { id: 'ermine', name: 'Ermine white', hex: 0xe8e4d6 },
  { id: 'jet', name: 'Jet black', hex: 0x141414 },
];

export interface WheelOption {
  id: string;
  name: string;
  price: number;
}

/** Wheel styles (ids are `WheelStyle`s from src/vehicles/meshes.ts). */
export const WHEELS: readonly WheelOption[] = [
  { id: 'steel', name: 'Painted steels', price: 60 },
  { id: 'hubcap', name: 'Chrome hubcaps', price: 90 },
  { id: 'disc', name: 'Black discs, chrome caps', price: 110 },
  { id: 'chrome', name: 'Ribbed chrome trims', price: 160 },
  { id: 'wire', name: 'Wire wheels with knock-offs', price: 240 },
];

export const TRIM_COLOURS: readonly Paint[] = [
  { id: 'cream', name: 'Cream', hex: 0xf0ece0 },
  { id: 'black', name: 'Black', hex: 0x141414 },
  { id: 'gold', name: 'Gold', hex: 0xc9a227 },
  { id: 'red', name: 'Red', hex: 0x9b2d20 },
];

/** Personal plates Des can get you (no questions asked). Glyphs: the car shader's plate font. */
export const PLATES: readonly string[] = ['KES 1', 'ACE 7', 'RUN 1', 'LOW 8', 'GT 70', 'BH 1970', 'FAST 1', 'MAX 9'];

export const PRICES = {
  respray: 180,
  metallic: 90,
  trim: 120,
  plate: 250,
} as const;

export interface TuneStage {
  stage: number;
  name: string;
  /** What Des does for it (the job card's note). */
  detail: string;
  price: number;
  tune: VehicleTune;
}

/** The tune: +6 % / +12 % power, +3 % / +6 % top speed. Small on purpose: a car still feels like itself. */
export const TUNES: readonly TuneStage[] = [
  { stage: 1, name: 'Stage 1 tune', detail: 'Twin carbs, a free-flow manifold', price: 650, tune: { power: 1.06, topSpeed: 1.03 } },
  { stage: 2, name: 'Stage 2 tune', detail: 'Hot cam, gas-flowed head', price: 1300, tune: { power: 1.12, topSpeed: 1.06 } },
];

/** Lenny's favour (all 16 matchbooks): Des knocks this much off. */
export const FAVOUR_DISCOUNT = 0.15;
export const UNLOCK_DES_FAVOUR = 'des-favour';

/** What the trim option is on this body ("Bonnet stripes", "Vinyl roof", …), or null if it has none. */
export function trimName(spec: VehicleSpec): string | null {
  if (spec.class === 'police') return null;
  if (spec.body === 'coupe') return 'Bonnet stripes';
  if (spec.body === 'saloon' || spec.body === 'exec') return 'Vinyl roof';
  if (spec.body === 'hatch' || spec.body === 'estate') return 'Contrast roof';
  if (spec.body === 'van') return 'Trade band';
  return null;
}

/** Des works on anything but police cars and the tipper lorry. */
export function canWorkOn(spec: VehicleSpec): boolean {
  return spec.class !== 'police' && spec.body !== 'lorry';
}

/**
 * Price after Lenny's favour and Des's own trade terms (contact ladder, WP-X1: `ladder` is a fraction off).
 * The two stack (whole pounds).
 */
export function price(base: number, favour: boolean, ladder = 0): number {
  const keep = (favour ? 1 - FAVOUR_DISCOUNT : 1) * (1 - Math.max(0, Math.min(0.5, ladder)));
  return keep === 1 ? base : Math.round(base * keep);
}

/** The handling tune for a build (undefined = stock). */
export function tuneFor(build: CarBuild): VehicleTune | undefined {
  return TUNES.find((t) => t.stage === build.tune)?.tune;
}

export function paintName(hex: number): string {
  return PAINTS.find((p) => p.hex === hex)?.name ?? 'its own colour';
}
